import { useCallback, useMemo } from 'react';
import { ListActivePaymentDto, PaymentPollingDto, PaymentState } from '../../generated-api';
import { usePollPaymentStatusList } from './usePaymentPollingApi';
import { isDefined } from '../utils/typeguards';
import { useToast } from '@ordr-as/umami-react/toast';
import PaymentToastContent, { COUNTDOWN_DURATION_MS } from '../components/PaymentToastContent';
import useIntl from './useIntl';
import { useLocation } from '../context/LocationContext';
import { CashDrawerStatus, useCashStatus } from './useCashStatus';
import { useQueryActivePayments } from './useQueryActivePayments';
import { useQueryClient } from '@tanstack/react-query';
import useDeviceId from './useDeviceId';
import { usePrintReceiptContext } from '../context/PrintReceiptContextProvider';
import { useScreenSize } from '../context/ScreenSizeContext';
import { usePinScreenSettings } from '../context/PinScreenSettingsContext';
import { CashPaymentStatus, useCashPaymentStatus } from './useCashPaymentStatus';
import { useHub } from '../context/HubProvider';

type Props = {
  focusedPayment?: ListActivePaymentDto;
  setFocusedPayment: (payment: ListActivePaymentDto | undefined) => void;
  onPaymentCaptured?: (payment: ListActivePaymentDto) => void;
  onPaymentFailed?: (payment: ListActivePaymentDto) => void;
};

export const useActivePayments = ({ focusedPayment, setFocusedPayment, onPaymentCaptured, onPaymentFailed }: Props) => {
  const { location, featureFlags } = useLocation();
  const intl = useIntl();
  const { data: activePayments } = useQueryActivePayments(location.locationId);
  const hub = useHub();
  const toasts = useToast();
  const { data: cashStatus } = useCashStatus();
  const queryClient = useQueryClient();
  const deviceId = useDeviceId();
  const { isPhone } = useScreenSize();
  const { handlePrintReceipt } = usePrintReceiptContext();
  const { initiateChangeUserOnCompletedSale } = usePinScreenSettings();
  const { setCashPaymentStatus } = useCashPaymentStatus();

  const isFailedPaymentPromptEnabled = featureFlags.failedPaymentPromptFeatureFlag?.enabled;

  const addActivePayment = (nextPayment: ListActivePaymentDto) => {
    if (nextPayment.paymentMethod.provider !== 'UNINTEGRATED') {
      hub.broadcast('payment.started', { locationId: location.locationId });
    }

    queryClient.setQueryData<typeof activePayments>(['active-payments', location.locationId], (prevPayments) => [
      ...(prevPayments || []),
      nextPayment,
    ]);
  };

  const removeActivePayment = useCallback(
    (payment: ListActivePaymentDto) => {
      queryClient.setQueryData(
        ['active-payments', location.locationId],
        (prevState: ListActivePaymentDto[] | undefined) =>
          prevState?.filter((activePayment) => activePayment.paymentReference != payment.paymentReference)
      );
      if (payment.paymentMethod.provider !== 'UNINTEGRATED') {
        hub.broadcast('payment.finished', { locationId: location.locationId });
      }
    },
    [hub, location.locationId, queryClient]
  );

  const printReceipt = useCallback(
    (payment: ListActivePaymentDto) => {
      handlePrintReceipt(payment.paymentReference || '', payment.paymentMethod.id);
      toasts.remove(payment.paymentReference || '');
    },
    [handlePrintReceipt, toasts]
  );

  const handlePaymentCaptured = useCallback(
    (payment: ListActivePaymentDto) => {
      if (!payment.paymentReference) {
        return;
      }

      removeActivePayment(payment);

      if (payment.paymentReference === focusedPayment?.paymentReference) {
        setFocusedPayment(undefined);
      }

      if (
        (cashStatus !== CashDrawerStatus.CONNECTED || payment.paymentMethod.paymentMethod !== 'CASH') &&
        payment.deviceId === deviceId
      ) {
        toasts.pushSuccess({
          id: payment.paymentReference,
          duration: COUNTDOWN_DURATION_MS,
          message: (
            <PaymentToastContent
              payment={payment}
              showCountdown
              swipeUp={isPhone}
              onDismiss={() => toasts.remove(payment.paymentReference || '')}
            />
          ),
          direction: isPhone ? 'top' : 'bottom',
          toastAction: {
            onAction: () => printReceipt(payment),
            actionLabel: 'Receipt',
          },
        });
      }

      if (
        cashStatus === CashDrawerStatus.CONNECTED &&
        payment.paymentMethod.paymentMethod === 'CASH' &&
        payment.deviceId === deviceId
      ) {
        setCashPaymentStatus(CashPaymentStatus.INITIATED);
      }

      setTimeout(() => {
        toasts.remove(payment.paymentReference || '');
        initiateChangeUserOnCompletedSale(payment.paymentMethod);
      }, COUNTDOWN_DURATION_MS);

      onPaymentCaptured?.(payment);
    },
    [
      cashStatus,
      deviceId,
      focusedPayment?.paymentReference,
      initiateChangeUserOnCompletedSale,
      isPhone,
      onPaymentCaptured,
      printReceipt,
      removeActivePayment,
      setCashPaymentStatus,
      setFocusedPayment,
      toasts,
    ]
  );

  const handlePaymentFailed = useCallback(
    (payment: ListActivePaymentDto) => {
      if (!payment.paymentReference) {
        return;
      }

      removeActivePayment(payment);

      if (payment.paymentReference === focusedPayment?.paymentReference) {
        setFocusedPayment(undefined);
      }

      if (payment.deviceId === deviceId && !isFailedPaymentPromptEnabled) {
        toasts.pushError({
          id: payment.paymentReference,
          message: <PaymentToastContent payment={payment} />,
          direction: isPhone ? 'top' : 'bottom',
          toastAction: {
            actionLabel: intl.formatMessage({ id: 'generic.fix' }),
            onAction: () => {
              if (payment && setFocusedPayment) {
                setFocusedPayment(payment);
              }
              toasts.remove(payment.paymentReference || '');
            },
          },
        });
      }

      if (isFailedPaymentPromptEnabled) {
        setFocusedPayment(payment);
      }

      onPaymentFailed?.(payment);
    },
    [
      deviceId,
      focusedPayment?.paymentReference,
      intl,
      isPhone,
      onPaymentFailed,
      removeActivePayment,
      setFocusedPayment,
      toasts,
      isFailedPaymentPromptEnabled,
    ]
  );

  const handlePaidOrFailedPayment = useCallback(
    (payment: ListActivePaymentDto) => {
      if (payment.paymentState === PaymentState.SUCCEEDED) {
        handlePaymentCaptured(payment);
      } else if (payment.paymentState === PaymentState.FAILED) {
        handlePaymentFailed(payment);
      }
    },
    [handlePaymentCaptured, handlePaymentFailed]
  );

  const handlePollingSuccess = useCallback(
    (data: PaymentPollingDto[]) => {
      data.forEach((pollingResult) => {
        const payment = activePayments?.find((payment) => payment.paymentReference === pollingResult.paymentReference);
        if (!payment) {
          return;
        }
        const paymentToProcess: ListActivePaymentDto = { ...payment, paymentState: pollingResult.paymentState };
        handlePaidOrFailedPayment(paymentToProcess);
      });
    },
    [activePayments, handlePaidOrFailedPayment]
  );

  const paymentsInProgress = useMemo(
    () =>
      activePayments !== undefined
        ? activePayments
            .filter(
              ({ paymentState }) => paymentState !== PaymentState.SUCCEEDED && paymentState !== PaymentState.FAILED
            )
            .map((ap) => ap.paymentReference)
            .filter(isDefined)
        : [],
    [activePayments]
  );

  const enablePolling = paymentsInProgress.length > 0;

  usePollPaymentStatusList(paymentsInProgress, enablePolling, handlePollingSuccess);

  return { activePayments, addActivePayment, removeActivePayment, handlePaidOrFailedPayment };
};
